import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { RiAccountCircleFill } from "react-icons/ri";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Bg from "../../../../assets/img/onboarding/onboarding-bg.png";
import { post } from "../../../../api-services/fetch";
import notification from "../../../../utilities/notification";
import Loading from "../../../common/Loading";
import TextInput from "../../../inputs/TextInput";
import { toggleScroll } from "../../../../utilities/general";
import useOnboardingStore from "../../../../context/onboarding/onboardingStore";
import Logo from "../../../../assets/img/logo.png";
import paths from "../../../../api-services/endpoints";
import { decodeToken } from "../../../../utilities/auth";
import OtpRateLimiter from "../../../common/OtpRateLimiter";
import DatePicker from "../../../layouts/DatePicker";
import CustomDatePicker from "../../../layouts/CustomDatePicker";
import FailedMessage from "./FailedMessage";
import { userPermission } from "../../../../utilities/userPermission";

// Dynamic Validation Schema
const getValidationSchema = (isOtp) => {
  return Yup.object({
    otp: isOtp
      ? Yup.string()
        .required("OTP is required")
        .length(6, "OTP must be exactly 6 digits")
      : Yup.string().notRequired(),
    dob: !isOtp
      ? Yup.date()
        .required("Date of Birth is required")
        .max(new Date(), "Date of Birth cannot be in the future")
      : Yup.date().notRequired()
  });
};

const VerifyBvnNumber = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [failedModal, setFailedModal] = useState(false);
  const [isOtp, setIsOtp] = useState(true);

  const { email, UserBVNDetails, merchantDetails, setMerchantDetails } =
    useOnboardingStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = location?.state?.isAuthenticated;
  const bvnvalidationID = UserBVNDetails?.id;
  const id = userPermission();
  useEffect(() => {
    toggleScroll();

    if (!UserBVNDetails?.id) {
      navigate("/profile-verification", { state: location.state });
    }
    if (!isAuthenticated) {
      navigate("/merchant-signup", { state: { step: 1, email } });
    }

    return () => toggleScroll();
  }, [UserBVNDetails, isAuthenticated, navigate, location, email]);

  const formatDateString = (dateString) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ];

    const date = new Date(dateString);
    if (isNaN(date)) return "Invalid Date"; // Handle invalid date inputs

    const day = date.getDate().toString().padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const verifyOtpOrDob = async (values) => {
    const { otp, dob } = values;
    const merchantId =
      merchantDetails?.id ||
      merchantDetails?.merchantDto?.id ||
      id;

    if (isOtp && (!otp || otp.length < 6)) {
      notification({
        title: "No OTP",
        message:
          "Please, enter the OTP that has been sent to your phone number.",
        type: "danger"
      });
      return;
    }

    if (!isOtp && !dob) {
      notification({
        title: "No Date of Birth",
        message: "Please, enter your Date of Birth to verify your BVN.",
        type: "danger"
      });
      return;
    }

    if (!merchantId || merchantDetails?.registrationStage < 7) {
      notification({
        title: "Invalid Profile",
        message: "Invalid profile. Please complete the registration process.",
        type: "danger"
      });
      navigate("/merchant-signup", { state: { step: 1, email } });
      return;
    }

    setIsLoading(true);
    try {
      const formattedDob = dob ? formatDateString(dob) : "";
      const res = await post({
        endpoint: paths.validateUnregisteredBusinessBVNOtp({
          merchantId,
          bvnvalidationID,
          otp: isOtp ? otp : undefined,
          dob: isOtp ? undefined : formattedDob
        }),
        body: { merchantId, bvnvalidationID, otp: isOtp ? otp : undefined,
          dob: isOtp ? undefined : formattedDob },
        auth: false,
        useDefaultSubKey: true
      });

      if (res?.status >= 200 && res?.status < 300) {
        const data = res?.data?.data;
        setMerchantDetails(data);
        notification({
          title: "Successful Verification",
          message: "Your verification was successful.",
          type: "success"
        });

        const ninNameLength = (data?.businessNinFullName?.length || 0) + 2;
        navigate("/merchant-registration-unregisteredBusiness", {
          state: {
            ...location.state,
            step: 8,
            bvnIdentifier: data?.id,
            isAuthenticated: true,
            maxBusinessNameLength: Math.max(0, 50 - ninNameLength)
          }
        });
      } else {
        setFailedModal(true)
        notification({
          title: "Error",
          message:
            res?.data?.message ||
            "An error occurred during verification. Please try again.",
          type: "danger"
        });
      }
    } catch (error) {
      notification({
        title: "Error",
        message: "An unexpected error occurred. Please try again.",
        type: "danger"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    const payload = {
      phoneNumber: UserBVNDetails?.id
    }
    setIsLoading(true);
    try {
      const res = await post({
        endpoint: paths.verifyPhoneRequest,
        body: payload,
        auth: false,
        useDefaultSubKey: true
      });

      if (res?.status >= 200 && res?.status < 300) {
        notification({
          title: "OTP Resent",
          message: "Verification OTP has been resent to your phone number.",
          type: "success"
        });
      } else {
        notification({
          title: "Error",
          message:
            res?.data?.message || "Failed to resend OTP. Please try again.",
          type: "danger"
        });
      }
    } catch (error) {
      notification({
        title: "Error",
        message: "An unexpected error occurred. Please try again.",
        type: "danger"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Loading />}
      <Formik
        initialValues={{ otp: "", dob: null }}
        onSubmit={verifyOtpOrDob}
        validationSchema={getValidationSchema(isOtp)}
      >
        {({
          handleChange,
          handleBlur,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          resetForm,
          values
        }) => (
          <Form>
            <section
              className="relative overflow-visible"
              style={{
                backgroundImage: `url(${Bg})`,
                backgroundSize: "cover",
                backgroundPosition: "center"
              }}
            >
              <div className="max-w-7xl mx-auto pt-10 px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-center justify-between w-full mdx2:w-auto">
                  <Link to="/">
                    <span className="sr-only">ALATpay</span>
                    <img
                      className="h-8 w-auto sm:h-10 mb-3"
                      src={Logo}
                      alt="Logo"
                    />
                  </Link>
                </div>
              </div>

              <div className="max-w-5xl mx-auto pt-10 pb-20 px-4 sm:px-6 lg:px-8">
                <div className="mx-auto w-full max-w-md">
                  <div className="flex items-center mb-2">
                    <RiAccountCircleFill className="text-alat-light mr-4 w-10 h-auto" />
                    <h3 className="font-bold text-2xl">
                      {isOtp
                        ? "Verify your phone number"
                        : "Verify BVN with your Date of Birth"}
                    </h3>
                  </div>
                  <p className="text-[#1F1F1F] text-center">
                    {isOtp ? (
                      <>
                        An OTP has been sent to{" "}
                        <span className="font-bold">
                          {UserBVNDetails?.phoneNumber || "your phone number.."}
                        </span>{" "}
                        for verification. Kindly enter the OTP in the text box
                        below.
                      </>
                    ) : (
                      "Enter your Date of Birth to verify your BVN"
                    )}
                  </p>

                  <div className="w-full p-5 bg-[#FDEAEC] rounded-lg mt-10">
                    {isOtp ? (
                      <>
                        <TextInput
                          labelClassName="font-bold"
                          className="w-full"
                          pattern="[0-9]*"
                          label="OTP"
                          name="otp"
                          type="password"
                          value={values.otp}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          maxLength={6}
                          placeholder="Enter verification code"
                        />
                        {touched.otp && errors.otp && (
                          <span className="text-red-500">{errors.otp}</span>
                        )}
                      </>
                    ) : (
                      <div className="w-full">
                        <CustomDatePicker
                          name="dob"
                          calendarTitle="Date of Birth"
                          labelClassName="font-semibold my-1"
                          onChange={(e) =>
                            handleChange({
                              target: {
                                name: "dob",
                                value: e
                              }
                            })
                          }
                          maxDate={new Date()}
                          minDate={null}
                          value={values.dob}
                          selectRange={false}
                          calendarInputClassName="w-full items-center justify-between bg-white"
                          containerClassName="w-full flex flex-col"
                          className={`w-full h-[42px] bg-white ${
                            errors.dob && touched.dob
                              ? "border-red-500"
                              : "border-gray-300"
                          } shadow-sm`}
                          calendarSVGClassName="top-2 right-0"
                          label="Date of Birth"
                        />
                        <span className="text-red-500">
                          {touched.dob ? errors.dob : ""}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mt-5">
                    <button
                      className="w-full bg-alat-light hover:bg-alat-light text-white font-bold py-3 px-4 rounded focus:outline-none focus:shadow-outline"
                      type="submit"
                      onClick={handleSubmit}
                    >
                      Submit
                    </button>
                    {isOtp ? (
                      <div
                        onClick={() => {
                          resetForm();
                          setIsOtp(false);
                        }}
                      >
                        <div className="w-full flex justify-around">
                          <span className="mx-auto mt-2">
                            Didn't receive an OTP?{" "}
                            <OtpRateLimiter
                              initialCount={60 * 5}
                              resendOtp={resendOtp}
                              text={<span>Resend OTP</span>}
                            />
                          </span>
                        </div>
                        <div className="mt-2 w-full flex justify-around">
                          <span>
                            Can't verify with OTP? &nbsp;
                            <span className="text-alat-red font-semibold cursor-pointer">
                              Verify with Date of Birth
                            </span>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="w-full flex justify-around cursor-pointer"
                        onClick={() => {
                          resetForm();
                          setIsOtp(true);
                        }}
                      >
                        <span className="mt-2 font-semibold text-alat-red">
                          Verify with OTP
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </Form>
        )}
      </Formik>
      {
        failedModal && <FailedMessage
          bvn={""}
          failedType="dob"
          btnAction={() =>
            setFailedModal(false)
          }
          btnLabel="Retry"
          onClose={()=>{
            setIsOtp(true);
            setFailedModal(false);
          }}
        />
      }
    </>
  );
};

export default VerifyBvnNumber;
